import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import { useStreamSelector } from "../useStreamSelector";
import { useComposerSelector } from "../composer/useComposerSelector";
import { StreamSelectors } from "../StreamSelectors";
import { useLanggraphStreamContext } from "../SmartStreamProvider";
import { useTurns } from "./hooks/useTurns";
import type { Turn } from "./hooks/useTurns";
import type { StreamSnapshot, QueuedMessage } from "../types";
import type { ComposerSnapshot } from "../composer/types";

/**
 * Optional custom submit handler context.
 * Stored separately so stream/composer contexts don't need to change.
 */
export interface ChatPersona {
  name: string;
  /** Avatar tile content (an icon or <img>); tiles style themselves. */
  avatar?: ReactNode;
  /** Override the avatar tile's styling (e.g. exact brand background). */
  tileClassName?: string;
}

interface ChatRootContextValue {
  onSubmit?: (sendMessage: () => void) => void;
  isInputDisabled?: boolean;
  onDisabledClick?: () => void;
  /** Names + avatars for the message lockups ("Houston" / "You"). */
  personas?: { assistant?: ChatPersona; user?: ChatPersona };
  /**
   * When true (default), Submit + Enter stay enabled while a stream is
   * in flight, and submissions during a run auto-route to the agent
   * inbox endpoint instead of creating a new run. The user perceives
   * a Claude-Code-style "type while the agent works" UX. See
   * plans/agent-inbox.md.
   *
   * Set false to restore the legacy "block while streaming" behavior.
   */
  concurrentSubmit?: boolean;
}

const ChatRootContext = createContext<ChatRootContextValue>({});

export const ChatRootProvider = ChatRootContext.Provider;

/**
 * Returns true when the Chat.Root has been told to disable all input
 * (e.g. user is out of credits). Individual input components read this
 * from context so consumers don't need to wire each one manually.
 */
/** Message-lockup personas, with generic defaults. */
export function useChatPersonas(): { assistant: ChatPersona; user: ChatPersona } {
  const personas = useContext(ChatRootContext).personas;
  return {
    assistant: personas?.assistant ?? { name: "Assistant" },
    user: personas?.user ?? { name: "You" },
  };
}

export function useChatIsInputDisabled(): boolean {
  return useContext(ChatRootContext).isInputDisabled ?? false;
}

/**
 * Returns the callback to invoke when a user clicks a disabled input,
 * or undefined if none was provided.
 */
export function useChatOnDisabledClick(): (() => void) | undefined {
  return useContext(ChatRootContext).onDisabledClick;
}

/**
 * Returns whether mid-stream submissions are allowed. Defaults to true:
 * Submit/Enter stay enabled while streuring, and submissions during a
 * run land on the agent inbox endpoint instead of creating a new run.
 */
export function useChatConcurrentSubmit(): boolean {
  return useContext(ChatRootContext).concurrentSubmit ?? true;
}

/**
 * Locally-queued messages awaiting drain — the user's typed input
 * during a streaming run, sent via the inbox path. Renders alongside
 * `messages` in the transcript with a "delivering" / "queued"
 * indicator until the drain materializes them as real conversation
 * messages and the SDK echoes them back via the stream.
 *
 * Each entry has:
 *   - `id` (stable local id)
 *   - `content` (the user's text)
 *   - `status` ("delivering" | "queued" | "failed")
 *   - `inboxRowId` (server-returned, present once POST resolved)
 *   - `error` (when status === "failed")
 *
 * Reconciliation is automatic: when an SDK message with
 * `additional_kwargs.inboxRowId` matching this entry's `inboxRowId`
 * appears in `messages`, the queued entry is dropped on the next
 * render. Consumers don't manage state — they just render the list.
 */
export function useChatQueuedMessages(): ReadonlyArray<QueuedMessage> {
  const store = useLanggraphStreamContext();
  return useSyncExternalStore(store.subscribe, store.getQueuedMessages, store.getQueuedMessages);
}

/**
 * Convenience hooks wrapping useStreamSelector + useComposerSelector.
 * These provide the same API shape as the old ChatContext hooks.
 */

export function useChatMessages() {
  return useStreamSelector(StreamSelectors.messages);
}

export function useChatTextMessages() {
  return useStreamSelector(StreamSelectors.textMessages);
}

export function useChatTurns(): Turn[] {
  const messages = useStreamSelector(StreamSelectors.messages);
  return useTurns(messages);
}

export function useChatIsStreaming() {
  return useStreamSelector(StreamSelectors.isStreaming);
}

export function useChatIsLoading() {
  return useStreamSelector(StreamSelectors.isLoading);
}

export function useChatIsLoadingHistory() {
  return useStreamSelector(StreamSelectors.isLoadingHistory);
}

export function useChatIsReady() {
  return useStreamSelector(StreamSelectors.isReady);
}

export function useChatError() {
  return useStreamSelector(StreamSelectors.error);
}

/**
 * Structured error reported by the agent's harness boundary, separate
 * from `useChatError` (which surfaces SDK / transport errors). Returns
 * `{ message, node }` when the agent classified a turn as a harness
 * failure, `undefined` otherwise.
 *
 * `Chat.ErrorBanner` reads both this and `useChatError` already — use
 * this hook directly when you want a custom error renderer that
 * branches on the structured shape (e.g. mapping `node` to a custom
 * UI per failing subsystem).
 */
export function useChatStateError() {
  return useStreamSelector(StreamSelectors.stateError);
}

export function useChatStop() {
  return useStreamSelector(StreamSelectors.stop);
}

export function useChatValues() {
  return useStreamSelector(StreamSelectors.values);
}

export function useChatComposer(): ComposerSnapshot {
  return useComposerSelector((s) => s);
}

/**
 * Get the submit function. If a custom onSubmit is provided to Chat.Root,
 * it wraps the default sendMessage — the caller decides whether/when to call it.
 */
export function useChatSubmit() {
  const ctx = useContext(ChatRootContext);
  const composerSendMessage = useComposerSelector((s) => s.sendMessage);

  if (!ctx.onSubmit) return composerSendMessage;

  const { onSubmit } = ctx;
  return () => onSubmit(composerSendMessage);
}

/**
 * Generic stream selector for custom queries.
 */
export function useChatStreamSelector<T>(selector: (s: StreamSnapshot) => T): T {
  return useStreamSelector(selector);
}

/**
 * Returns the active HumanInterrupt payload, or null if the graph isn't
 * currently paused on an interrupt. Reads `snapshot.interrupt?.value` —
 * the LangGraph SDK wraps interrupts in `{ value, when, resumable, ns }`;
 * `value` is the producer's payload (typically a HumanInterrupt).
 *
 * Render `<InterruptCard>` based on this hook's return value.
 */
export function useChatInterrupt<T = unknown>(): T | null {
  const interrupt = useStreamSelector(StreamSelectors.interrupt);
  return ((interrupt?.value as T | undefined) ?? null) as T | null;
}

/**
 * Returns a function that resumes the paused graph with a HumanResponse.
 * Wraps `snapshot.submit(null, { command: { resume } })` — the SDK's
 * idiom for "no new input, just resume with this value." Drop into
 * `<InterruptCard onResponse={useChatRespondToInterrupt()} ...>`.
 */
export function useChatRespondToInterrupt<R = unknown>(): (response: R) => Promise<void> {
  const submit = useStreamSelector((s) => s.submit);
  return async (response: R) => {
    await submit(null, { command: { resume: response } });
  };
}
